import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Plus, Trash2, Save } from 'lucide-react';
import { getSiteSettings, updateSiteSettings } from '../services/api';
import { toast } from 'sonner';

export const SiteSettingsManager = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await getSiteSettings();
      setSettings(data);
    } catch (error) {
      toast.error('Failed to load settings');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await updateSiteSettings(settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
      console.error(error);
    }
  };

  const addNavItem = () => {
    setSettings({
      ...settings,
      nav_items: [...settings.nav_items, { label: '', path: '' }]
    });
  };

  const updateNavItem = (index, field, value) => {
    const updated = [...settings.nav_items];
    updated[index][field] = value;
    setSettings({ ...settings, nav_items: updated });
  };

  const removeNavItem = (index) => {
    setSettings({
      ...settings,
      nav_items: settings.nav_items.filter((_, i) => i !== index)
    });
  };

  const addSocialLink = () => {
    setSettings({
      ...settings,
      social_links: [...settings.social_links, { platform: '', url: '' }]
    });
  };

  const updateSocialLink = (index, field, value) => {
    const updated = [...settings.social_links];
    updated[index][field] = value;
    setSettings({ ...settings, social_links: updated });
  };

  const removeSocialLink = (index) => {
    setSettings({
      ...settings,
      social_links: settings.social_links.filter((_, i) => i !== index)
    });
  };

  const addOfficeHour = () => {
    setSettings({
      ...settings,
      office_hours: [...settings.office_hours, '']
    });
  };

  const updateOfficeHour = (index, value) => {
    const updated = [...settings.office_hours];
    updated[index] = value;
    setSettings({ ...settings, office_hours: updated });
  };

  const removeOfficeHour = (index) => {
    setSettings({
      ...settings,
      office_hours: settings.office_hours.filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return <div className="text-center py-8">Loading settings...</div>;
  }

  if (!settings) {
    return <div className="text-center py-8">Failed to load settings</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header Settings */}
      <Card className="dark:bg-gray-900 dark:border-gray-800">
        <CardHeader>
          <CardTitle className="dark:text-white">Header Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Logo Image URL (Optional)</Label>
            <Input
              value={settings.logo_image || ''}
              onChange={(e) => setSettings({ ...settings, logo_image: e.target.value })}
              placeholder="https://... (Leave empty to use text logo)"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              If provided, this image will replace the text logo in header and footer
            </p>
          </div>
          {settings.logo_image && (
            <>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Logo Width (pixels)</Label>
                  <Input
                    type="number"
                    value={settings.logo_width || 0}
                    onChange={(e) => setSettings({ ...settings, logo_width: parseInt(e.target.value) || 0 })}
                    placeholder="0 (auto)"
                    min="0"
                    max="500"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Set to 0 for automatic width
                  </p>
                </div>
                <div>
                  <Label>Logo Height (pixels)</Label>
                  <Input
                    type="number"
                    value={settings.logo_height || 48}
                    onChange={(e) => setSettings({ ...settings, logo_height: parseInt(e.target.value) || 48 })}
                    placeholder="48"
                    min="20"
                    max="200"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Default: 48px
                  </p>
                </div>
              </div>
              <div className="mt-2 p-4 bg-gray-50 dark:bg-gray-800 rounded">
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Preview:</p>
                <img 
                  src={settings.logo_image} 
                  alt="Logo preview" 
                  className="object-contain"
                  style={{ 
                    height: `${settings.logo_height || 48}px`,
                    width: settings.logo_width ? `${settings.logo_width}px` : 'auto'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <p className="text-xs text-red-500 hidden">Invalid image URL</p>
              </div>
            </>
          )}
          <div className="flex items-center space-x-2 mt-4">
            <Switch
              id="show_logo_text"
              checked={settings.show_logo_text}
              onCheckedChange={(checked) => setSettings({ ...settings, show_logo_text: checked })}
            />
            <Label htmlFor="show_logo_text">Show business name next to logo</Label>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Business Name</Label>
              <Input
                value={settings.logo_text}
                onChange={(e) => setSettings({ ...settings, logo_text: e.target.value })}
                placeholder="Otter River Properties"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Displays next to logo if enabled above
              </p>
            </div>
            <div>
              <Label>Tagline</Label>
              <Input
                value={settings.tagline}
                onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
                placeholder="Lynchburg, VA"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Appears below business name
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Navigation Items */}
      <Card className="dark:bg-gray-900 dark:border-gray-800">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="dark:text-white">Navigation Menu</CardTitle>
            <Button onClick={addNavItem} size="sm" variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {settings.nav_items.map((item, index) => (
            <div key={index} className="flex gap-2 items-end">
              <div className="flex-1">
                <Label className="text-sm">Label</Label>
                <Input
                  value={item.label}
                  onChange={(e) => updateNavItem(index, 'label', e.target.value)}
                  placeholder="Home"
                />
              </div>
              <div className="flex-1">
                <Label className="text-sm">Path</Label>
                <Input
                  value={item.path}
                  onChange={(e) => updateNavItem(index, 'path', e.target.value)}
                  placeholder="/"
                />
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => removeNavItem(index)}
                className="text-red-600"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card className="dark:bg-gray-900 dark:border-gray-800">
        <CardHeader>
          <CardTitle className="dark:text-white">Contact Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Phone</Label>
            <Input
              value={settings.phone}
              onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
              placeholder="(434) 555-0123"
            />
          </div>
          <div>
            <Label>Email</Label>
            <Input
              type="email"
              value={settings.email}
              onChange={(e) => setSettings({ ...settings, email: e.target.value })}
              placeholder="info@cozyproperties.com"
            />
          </div>
          <div>
            <Label>Address</Label>
            <Input
              value={settings.address}
              onChange={(e) => setSettings({ ...settings, address: e.target.value })}
              placeholder="123 Main Street, Suite 200, Lynchburg, VA 24504"
            />
          </div>
          <div>
            <Label>Appointments Email</Label>
            <Input
              type="email"
              value={settings.appointments_email || settings.email}
              onChange={(e) => setSettings({ ...settings, appointments_email: e.target.value })}
              placeholder="appointments@cozyproperties.com"
            />
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Email where appointment requests will be sent
            </p>
          </div>
        </CardContent>
      </Card>

      {/* reCAPTCHA Settings */}
      <Card className="dark:bg-gray-900 dark:border-gray-800">
        <CardHeader>
          <CardTitle className="dark:text-white">reCAPTCHA v3 Protection</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="recaptcha-enabled"
              checked={settings.recaptcha_enabled || false}
              onChange={(e) => setSettings({ ...settings, recaptcha_enabled: e.target.checked })}
              className="h-4 w-4"
            />
            <Label htmlFor="recaptcha-enabled">Enable reCAPTCHA Protection</Label>
          </div>

          {settings.recaptcha_enabled && (
            <>
              <div>
                <Label>reCAPTCHA Site Key (Public)</Label>
                <Input
                  value={settings.recaptcha_site_key || ''}
                  onChange={(e) => setSettings({ ...settings, recaptcha_site_key: e.target.value })}
                  placeholder="6Lc..."
                />
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Public key used on your website
                </p>
              </div>

              <div>
                <Label>reCAPTCHA Secret Key (Private)</Label>
                <Input
                  type="password"
                  value={settings.recaptcha_secret_key || ''}
                  onChange={(e) => setSettings({ ...settings, recaptcha_secret_key: e.target.value })}
                  placeholder="6Lc..."
                />
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Secret key for server-side verification
                </p>
              </div>

              <div className="bg-otter-green-50 dark:bg-otter-green-800/20 p-4 rounded">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>Get your keys:</strong> Visit{' '}
                  <a
                    href="https://www.google.com/recaptcha/admin/create"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    Google reCAPTCHA Admin
                  </a>
                  <br />
                  Select reCAPTCHA v3, add your domain, and copy both keys here.
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Footer Settings */}
      <Card className="dark:bg-gray-900 dark:border-gray-800">
        <CardHeader>
          <CardTitle className="dark:text-white">Footer Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Footer About Text</Label>
            <Textarea
              value={settings.footer_about}
              onChange={(e) => setSettings({ ...settings, footer_about: e.target.value })}
              rows={2}
              placeholder="Your trusted partner in property management..."
            />
          </div>
        </CardContent>
      </Card>

      {/* Social Links */}
      <Card className="dark:bg-gray-900 dark:border-gray-800">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="dark:text-white">Social Media Links</CardTitle>
            <Button onClick={addSocialLink} size="sm" variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Add Link
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {settings.social_links.map((link, index) => (
            <div key={index} className="flex gap-2 items-end">
              <div className="flex-1">
                <Label className="text-sm">Platform</Label>
                <Input
                  value={link.platform}
                  onChange={(e) => updateSocialLink(index, 'platform', e.target.value)}
                  placeholder="Facebook"
                />
              </div>
              <div className="flex-1">
                <Label className="text-sm">URL</Label>
                <Input
                  value={link.url}
                  onChange={(e) => updateSocialLink(index, 'url', e.target.value)}
                  placeholder="https://facebook.com/..."
                />
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => removeSocialLink(index)}
                className="text-red-600"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Office Hours */}
      <Card className="dark:bg-gray-900 dark:border-gray-800">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="dark:text-white">Office Hours</CardTitle>
            <Button onClick={addOfficeHour} size="sm" variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Add Hour
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {settings.office_hours.map((hour, index) => (
            <div key={index} className="flex gap-2 items-center">
              <Input
                value={hour}
                onChange={(e) => updateOfficeHour(index, e.target.value)}
                placeholder="Mon-Fri: 9:00 AM - 5:00 PM"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => removeOfficeHour(index)}
                className="text-red-600"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} className="bg-otter-green hover:bg-otter-green-600 text-white">
          <Save className="h-4 w-4 mr-2" />
          Save All Settings
        </Button>
      </div>
    </div>
  );
};
